import { Injectable } from '@angular/core';
import { WebServiceService } from './web-service.service';
import { LocalStorageService } from './storage/local-storage.service';

/**
 * IR module: ir_fields_table visibility, line totals, and view/modal display prep.
 * Uses WebServiceService for HTTP and number formatting only.
 */
@Injectable()
export class IrFieldsService {
  private readonly IR_FIELD_COLUMN_MAP: any = {
    category: 'category_name',
    sub_category: 'subcategory_name',
    required_by: 'product_required_by',
    remarks: 'remarks',
    total_value: 'total_value',
  };

  constructor(
    private webService: WebServiceService,
    private localStorageService: LocalStorageService
  ) {}

  isIrReportTableId(tableId: string): boolean {
    return tableId === 'irreport' || tableId === 'ir_history_report';
  }

  hideIrGrandForeignColumns(columnList: any[]): void {
    if (!columnList || !columnList.length) {
      return;
    }
    for (const col of columnList) {
      if (col.value === 'foreign_amount') {
        col.is_view = false;
      }
    }
  }

  normalizeIrViewLineItem(item: any, allowedDigits?: string): void {
    if (!item) {
      return;
    }
    const dig = allowedDigits || '2';
    const unitParsed = parseFloat(this.webService.convertNaN(item.unit_price, dig));
    item.unit_price = this.webService.convertNaN(unitParsed, dig)
      ? this.webService.convertNaN(unitParsed, dig)
      : '0';
    item.product_qty =
      item.product_qty != null && item.product_qty !== ''
        ? item.product_qty
        : item.item_qty;
    let total = item.total_value;
    if (total == null || total === '' || Number(total) === 0) {
      total =
        parseFloat(this.webService.convertNaN(item.unit_price)) *
        parseFloat(this.webService.convertNaN(item.product_qty));
    }
    item.total_value = this.webService.convertNaN(total);
  }

  prepareIrModalItems(irItems: any[]): void {
    if (!irItems || !irItems.length) {
      return;
    }
    let slNo = 1;
    for (const j of irItems) {
      j.sl_no = slNo;
      j.product_catalog_id = j.product_catalog_id
        ? j.product_catalog_id
        : j.product && j.product.product_catalog_id;
      if (j.product) {
        j.product_name = j.product.product_name;
        j.productcode = j.product.productcode;
        j.product_name = j.product_name + '(' + j.productcode + ')';
        j.product_type = j.product.product_type ? j.product.product_type : '';
      }
      this.normalizeIrViewLineItem(j);
      slNo++;
    }
  }

  ensureIrHeaderTotals(data: any): void {
    if (!data) {
      return;
    }
    data.grand_total =
      data.grand_total != null && data.grand_total !== '' ? data.grand_total : 0;
    data.total_qty =
      data.total_qty != null && data.total_qty !== '' ? data.total_qty : 0;
  }

  prepareIrReportDataForDisplay(data: any): void {
    if (!data) {
      return;
    }
    if (Array.isArray(data.ir_items) && data.ir_items.length) {
      this.prepareIrModalItems(data.ir_items);
    }
    this.computeIrGrandTotalsFromItems(data);
    this.ensureIrHeaderTotals(data);
  }

  isIrFieldsTableApplied(company_setup: any): boolean {
    if (!company_setup) {
      return false;
    }
    const table = Array.isArray(company_setup.ir_fields_table)
      ? company_setup.ir_fields_table
      : [];
    return company_setup._irFieldsAppliedKey === table;
  }

  applyIrFieldsTable(company_setup: any, force: boolean = false): void {
    if (!company_setup || typeof company_setup !== 'object') {
      return;
    }
    if (!force && this.isIrFieldsTableApplied(company_setup)) {
      return;
    }
    const table = Array.isArray(company_setup.ir_fields_table)
      ? company_setup.ir_fields_table
      : [];
    company_setup.ir_fields_map = company_setup.ir_fields_map || {};
    for (const row of table) {
      if (row && row.key) {
        company_setup.ir_fields_map[row.key] = row;
      }
    }
    const hasTable = table.length > 0;
    const moduleFlag = (key: string, legacy?: string) => {
      if (hasTable) {
        const row = company_setup.ir_fields_map[key];
        if (row) {
          return this.isIrFieldFlagOn(row, 'is_visible_in_module') ? '1' : '0';
        }
        return legacy === '1' ? '1' : '0';
      }
      return legacy === '1' ? '1' : '0';
    };
    const irModuleLegacy = (prFlag: string) => (hasTable ? '0' : prFlag);
    company_setup.tot_val_visible_in_ir = moduleFlag(
      'total_value',
      irModuleLegacy(company_setup.tot_val_visible_in_pr)
    );
    company_setup.grand_tot_visible_in_ir = moduleFlag(
      'grand_total',
      irModuleLegacy(company_setup.grand_tot_visible_in_pr)
    );
    company_setup.ir_category_visible_in_module = moduleFlag('category', '1');
    company_setup.ir_sub_category_visible_in_module = moduleFlag('sub_category', '1');
    company_setup.ir_required_by_visible_in_module = moduleFlag('required_by', '1');
    company_setup.ir_remarks_visible_in_module = moduleFlag('remarks', '1');
    company_setup.tot_qty_visible_in_ir = moduleFlag(
      'total_qty',
      company_setup.tot_qty_visible_in_pr
    );
    company_setup._irFieldsAppliedKey = table;
  }

  mergeIrFieldsTableFromResponse(company_setup: any, tabwiseResponse: any): void {
    if (!company_setup) {
      return;
    }
    const fields =
      tabwiseResponse && tabwiseResponse.ir_fields_table
        ? tabwiseResponse.ir_fields_table
        : tabwiseResponse &&
            tabwiseResponse.response &&
            tabwiseResponse.response.ir_fields_table
          ? tabwiseResponse.response.ir_fields_table
          : null;
    if (fields) {
      company_setup.ir_fields_table = fields;
    }
    company_setup._irFieldsAppliedKey = null;
    this.applyIrFieldsTable(company_setup, true);
  }

  isIrFieldVisibleInModule(company_setup: any, key: string): boolean {
    if (!company_setup) {
      return false;
    }
    const table = Array.isArray(company_setup.ir_fields_table)
      ? company_setup.ir_fields_table
      : [];
    if (table.length > 0) {
      return this.isIrFieldFlagOn(
        company_setup.ir_fields_map && company_setup.ir_fields_map[key],
        'is_visible_in_module'
      );
    }
    if (key === 'total_value') {
      return (
        company_setup.tot_val_visible_in_ir === '1' ||
        company_setup.tot_val_visible_in_pr === '1'
      );
    }
    if (key === 'grand_total') {
      return (
        company_setup.grand_tot_visible_in_ir === '1' ||
        company_setup.grand_tot_visible_in_pr === '1'
      );
    }
    return true;
  }

  isIrColumnVisibleInModule(company_setup: any, columnValue: string): boolean {
    if (!company_setup) {
      return false;
    }
    for (const key in this.IR_FIELD_COLUMN_MAP) {
      if (this.IR_FIELD_COLUMN_MAP[key] === columnValue) {
        return this.isIrFieldVisibleInModule(company_setup, key);
      }
    }
    return true;
  }

  getIrFieldDisplayName(
    company_setup: any,
    key: string,
    defaultName: string
  ): string {
    const row =
      company_setup &&
      company_setup.ir_fields_map &&
      company_setup.ir_fields_map[key];
    return row && row.name ? row.name : defaultName;
  }

  bindIrViewTableDetails(
    tableDetails: any,
    company_setup: any,
    data: any
  ): any {
    const payload = data ? data : {};
    this.computeIrGrandTotalsFromItems(payload);
    this.ensureIrHeaderTotals(payload);
    return Object.assign({}, tableDetails || {}, {
      data: payload,
      setup: company_setup ? company_setup : {},
      is_setup: true,
    });
  }

  hasIrFieldsTableConfig(company_setup: any): boolean {
    return (
      company_setup &&
      Array.isArray(company_setup.ir_fields_table) &&
      company_setup.ir_fields_table.length > 0
    );
  }

  computeIrGrandTotalsFromItems(data: any, force: boolean = false): void {
    if (!data) {
      return;
    }
    if (!force && data._irTotalsComputed) {
      return;
    }
    const items = Array.isArray(data.ir_items) ? data.ir_items : [];
    if (!items.length) {
      data._irTotalsComputed = true;
      return;
    }
    const headerGrand = data.grand_total;
    const headerQty = data.total_qty;
    const needGrand =
      headerGrand == null || headerGrand === '' || Number(headerGrand) === 0;
    const needQty =
      headerQty == null || headerQty === '' || Number(headerQty) === 0;
    if (!needGrand && !needQty) {
      data._irTotalsComputed = true;
      return;
    }
    let grandTotal = 0;
    let totalQty = 0;
    for (const i of items) {
      const qty =
        i.product_qty != null && i.product_qty !== ''
          ? i.product_qty
          : i.item_qty;
      totalQty += parseFloat(this.webService.convertNaN(qty)) || 0;
      let lineTotal = i.total_value;
      if (
        lineTotal == null ||
        lineTotal === '' ||
        Number(lineTotal) === 0
      ) {
        lineTotal =
          parseFloat(this.webService.convertNaN(i.unit_price)) *
          parseFloat(this.webService.convertNaN(qty));
      }
      grandTotal += parseFloat(this.webService.convertNaN(lineTotal)) || 0;
    }
    if (needGrand) {
      data.grand_total = this.webService.convertNaN(grandTotal);
    }
    if (needQty) {
      data.total_qty = this.webService.convertNaN(totalQty);
    }
    data._irTotalsComputed = true;
  }

  loadIrCompanyMetaTabwise(
    company_setup: any,
    callback?: (setup: any) => void
  ): void {
    const req: any = {
      company_id:
        this.localStorageService.getEmpCompanyId() == undefined
          ? '0'
          : this.localStorageService.getEmpCompanyId(),
    };
    this.webService
      .commonPostMethod(
        'Update_company_meta/ajax_getall_bycompany_tabwise_view',
        req
      )
      .subscribe(
        data => {
          if (data && data.status) {
            this.mergeIrFieldsTableFromResponse(
              company_setup,
              data.response || data
            );
          } else {
            this.applyIrFieldsTable(company_setup);
          }
          if (callback) {
            callback(company_setup);
          }
        },
        () => {
          this.applyIrFieldsTable(company_setup);
          if (callback) {
            callback(company_setup);
          }
        }
      );
  }

  private isIrFieldFlagOn(field: any, prop: string): boolean {
    if (!field) {
      return false;
    }
    const val = field[prop];
    if (val === false || val === '0' || val === 0 || val === 'false') {
      return false;
    }
    return val === true || val === '1' || val === 1;
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src2594816822/src/src/app/shared/services/ir-fields.service.ts